import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { useState, useCallback } from "react";

// Client testimonials. Names are abbreviated for confidentiality. These are
// NOT presented as Google reviews and we do NOT publish an AggregateRating
// schema off the back of them — see .lovable/seo-report.md § "Reviews".
const testimonials = [
  {
    name: "C.B.",
    role: "Fuel Service Franchisee",
    location: "Cape Town",
    rating: 5,
    text: "Running a petrol station means high-volume transactions daily. Their bookkeeping is meticulous and my SARS compliance has never been cleaner. Highly recommend.",
  },
  {
    name: "L.F.",
    role: "Fuel Service Franchisee",
    location: "Cape Town",
    rating: 5,
    text: "Managing a fuel service station is complex. They understand the business and keep everything compliant with zero stress.",
  },
  {
    name: "Dr H.",
    role: "Medical Practitioner",
    location: "Cape Town",
    rating: 5,
    text: "They handle our payroll for 3 staff, VAT, and all SARS submissions. As a busy practice, I need reliability — and they deliver every month without fail.",
  },
  {
    name: "E.V.",
    role: "Coffee Shop Owner",
    location: "Cape Town",
    rating: 5,
    text: "I opened my second branch and needed proper books fast. They had me set up within a week — payroll, UIF, everything. Worth every cent.",
  },
  {
    name: "A.J.",
    role: "Pharmacy Owner",
    location: "Cape Town",
    rating: 5,
    text: "Switched from doing my own books and it's been a game-changer. Professional, affordable, and they actually explain things in plain language.",
  },
  {
    name: "K.H.",
    role: "Architect",
    location: "Cape Town",
    rating: 5,
    text: "Managing a practice is demanding enough. They take care of all the financials so I can focus on my projects. Couldn't ask for better service.",
  },
];

export function Testimonials() {
  const { ref, isVisible } = useScrollReveal();
  const [currentIndex, setCurrentIndex] = useState(0);

  const getVisibleCount = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 1024) return 3;
    if (typeof window !== "undefined" && window.innerWidth >= 640) return 2;
    return 1;
  };

  const [visibleCount] = useState(getVisibleCount);
  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-muted via-accent/20 to-muted">
      <div className="container mx-auto px-4">
        <div
          ref={ref}
          className={`text-center mb-10 max-w-3xl mx-auto scroll-reveal ${isVisible ? "revealed" : ""}`}
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            What our clients say
          </h2>
          <p className="mt-3 text-muted-foreground">
            Feedback from South African business owners we work with.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            disabled={currentIndex >= maxIndex}
            className="absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="overflow-hidden px-2">
            <div
              className="flex gap-5 transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
              }}
            >
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="flex-shrink-0 w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]"
                >
                  <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col hover:border-primary/30 hover:shadow-card-hover transition-all duration-300">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground shrink-0">
                        {t.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground leading-tight">{t.name}</p>
                        <p className="text-xs text-muted-foreground">Verified client</p>
                      </div>
                    </div>

                    <div className="flex gap-0.5 mb-3" aria-label={`Rated ${t.rating} out of 5`}>
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[hsl(45,93%,47%)] text-[hsl(45,93%,47%)]" />
                      ))}
                    </div>

                    <p className="text-sm text-foreground leading-relaxed flex-1">
                      {t.text}
                    </p>

                    <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-border">
                      {t.role} · {t.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === currentIndex
                    ? "bg-primary w-6"
                    : "bg-border hover:bg-muted-foreground"
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          * Names abbreviated for confidentiality. These are direct client testimonials, not Google reviews.
        </p>
      </div>
    </section>
  );
}
